import apiBaseUrl from '../../utils/config.js';

import { authFetch } from '../../utils/authFetch.js';



/**

 * Safely parse an API response that may contain:

 *

 * - JSON

 * - plain text

 * - no response body

 */

async function parseResponse(response) {

  const text = await response.text();



  if (!text) {

    return null;

  }



  try {

    return JSON.parse(text);

  } catch {

    return {

      message: text,

    };

  }

}



/**

 * Create a consistent frontend Error object from an unsuccessful

 * backend response.

 */

function createRequestError(

  response,

  data,

  fallbackMessage

) {

  const message =

    data?.message ||

    data?.error ||

    fallbackMessage ||

    `HTTP ${response.status}`;



  const error =

    new Error(message);



  error.status =

    response.status;



  error.data =

    data;



  return error;

}



/**

 * Validate a clinical-note identifier before making a request.

 */

function requireClinicalNoteId(

  clinicalNoteId

) {

  const numericId =

    Number(clinicalNoteId);



  if (

    !Number.isInteger(

      numericId

    ) ||

    numericId <= 0

  ) {

    throw new Error(

      'A valid clinical note ID is required.'

    );

  }



  return numericId;

}



/**

 * Validate a superbill identifier before making a request.

 */

function requireSuperbillId(

  superbillId

) {

  const numericId =

    Number(superbillId);



  if (

    !Number.isInteger(

      numericId

    ) ||

    numericId <= 0

  ) {

    throw new Error(

      'A valid superbill ID is required.'

    );

  }



  return numericId;

}



/**
 * Validate an authorization identifier before making a request.
 */
function requireAuthorizationId(
  authId
) {
  const numericId = Number(authId);

  if (
    !Number.isInteger(numericId) ||
    numericId <= 0
  ) {
    throw new Error(
      'A valid authorization ID is required.'
    );
  }

  return numericId;
}

/**
 * Validate a clinical-episode identifier before making a request.
 */
function requireClinicalEpisodeId(
  clinicalEpisodeId
) {
  const numericId = Number(clinicalEpisodeId);

  if (
    !Number.isInteger(numericId) ||
    numericId <= 0
  ) {
    throw new Error(
      'A valid clinical episode ID is required.'
    );
  }

  return numericId;
}

/**

 * Validate a positive template identifier.

 */

function requireTemplateId(

  templateId,

  label

) {

  const numericId =

    Number(templateId);



  if (

    !Number.isInteger(

      numericId

    ) ||

    numericId <= 0

  ) {

    throw new Error(

      `A valid ${label} is required.`

    );

  }



  return numericId;

}



/**

 * Normalize optional text sent to the backend.

 */

function normalizeOptionalText(

  value

) {

  if (

    value === null ||

    value === undefined

  ) {

    return null;

  }



  const normalized =

    String(value).trim();



  return normalized || null;

}



/**

 * Normalize optional enum values sent to the backend.

 */

function normalizeOptionalEnum(

  value

) {

  if (

    value === null ||

    value === undefined

  ) {

    return null;

  }



  const normalized =

    String(value).trim();



  return normalized || null;

}



/**

 * Normalize an optional yyyy-MM-dd date.

 */

function normalizeOptionalDate(

  value

) {

  if (

    value === null ||

    value === undefined

  ) {

    return null;

  }



  const normalized =

    String(value).trim();



  return normalized || null;

}



/**

 * Normalize an optional positive whole-number ID.

 */

function normalizeOptionalInteger(

  value,

  label

) {

  if (

    value === null ||

    value === undefined ||

    String(value).trim() === ''

  ) {

    return null;

  }



  const numericValue =

    Number(value);



  if (

    !Number.isInteger(

      numericValue

    ) ||

    numericValue <= 0

  ) {

    throw new Error(

      `${label} must be a positive whole number.`

    );

  }



  return numericValue;

}



/**

 * Frontend API service for the clinical-note module.

 *

 * IMPORTANT:

 *

 * All mutations belonging to the same ClinicalNoteMain are

 * serialized through one per-note queue.

 *

 * This prevents independent section autosaves from racing against

 * each other with the same optimistic-lock version.

 *

 * Ordinary section autosaves obtain the latest entityVersion when

 * their turn in the queue begins.

 *

 * Workflow/signing/warning actions intentionally retain the version

 * supplied by the caller because those actions must apply to the

 * exact note state the user reviewed.

 */

/**
 * Normalize an optional positive whole number.
 *
 * Used for structured clinical values such as treatment frequency
 * and duration. Blank values remain null so partially completed
 * drafts can still autosave safely.
 */
function normalizeOptionalPositiveWholeNumber(
  value,
  label
) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return null;
  }

  const numericValue =
    Number(value);

  if (
    !Number.isInteger(
      numericValue
    ) ||
    numericValue <= 0
  ) {
    throw new Error(
      `${label} must be a positive whole number.`
    );
  }

  return numericValue;
}


class ClinicalNoteService {

  constructor() {

    this.baseUrl =

      `${apiBaseUrl}/clinical-notes`;



    /**

     * Map:

     *

     * clinicalNoteId -> last queued mutation Promise

     */

    this.mutationQueues =

      new Map();

  }



  /**

   * JSON request headers.

   */

  getJsonHeaders() {

    return {

      'Content-Type':

        'application/json',

    };

  }



  /**

   * GET request headers where Content-Type is unnecessary.

   */

  getPlainHeaders() {

    return {};

  }



  // =========================================================

  // CROSS-SECTION MUTATION COORDINATION

  // =========================================================



  /**

   * Serialize all mutations that belong to one clinical note.

   *

   * A failed earlier request does not permanently poison the queue.

   * The next mutation may still run.

   */

  async runSerializedMutation(

    clinicalNoteId,

    mutation

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const previous =

      this.mutationQueues.get(

        id

      ) ||

      Promise.resolve();



    const queued =

      previous

        .catch(() => {

          /*

           * Allow the queue to continue after an earlier failed

           * mutation.

           */

        })

        .then(

          async () =>

            mutation()

        );



    this.mutationQueues.set(

      id,

      queued

    );



    try {

      return await queued;

    } finally {

      /*

       * Only remove the queue entry if no newer mutation was

       * appended while this one was executing.

       */

      if (

        this.mutationQueues.get(

          id

        ) === queued

      ) {

        this.mutationQueues.delete(

          id

        );

      }

    }

  }



  /**

   * Run an ordinary editable-section save.

   *

   * The newest parent ClinicalNoteMain.entityVersion is loaded only

   * after all earlier note mutations have completed.

   *

   * That fresh version replaces the component's older local

   * expectedVersion for this section save.

   */

  async runSerializedSectionSave(

    clinicalNoteId,

    mutation

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    return this.runSerializedMutation(

      id,

      async () => {

        const note =

          await this.getClinicalNote(

            id

          );



        const currentVersion =

          note?.entityVersion;



        if (

          currentVersion ===

            null ||

          currentVersion ===

            undefined

        ) {

          throw new Error(

            'The current clinical note version could not be loaded before autosave.'

          );

        }



        return mutation(

          currentVersion

        );

      }

    );

  }



  /**
   * Read the existing clinical notes within one clinical episode.
   *
   * The backend authorizes access and returns only note navigation metadata.
   * This operation does not create or update documentation.
   */
  async getEpisodeVisits(clinicalEpisodeId) {
    const id = requireClinicalEpisodeId(clinicalEpisodeId);

    const response = await authFetch(
      `${this.baseUrl}/episode/${id}/visits`,
      {
        method: 'GET',
        headers: this.getPlainHeaders(),
      }
    );

    const data = await parseResponse(response);

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Clinical episode visits could not be loaded.'
      );
    }

    if (!Array.isArray(data)) {
      throw new Error('Clinical episode visit list has an invalid response format.');
    }

    return data;
  }

  /**
   * Wait for all mutations already queued for this clinical note.
   *
   * Important: editors must FIRST flush their debounced changes. This
   * method cannot see edits that have not yet entered the service queue.
   *
   * A mutation rejected by the backend is intentionally propagated so
   * the caller can keep the clinician on the current note.
   */
  async waitForPendingMutations(clinicalNoteId) {
    const id = requireClinicalNoteId(clinicalNoteId);
    const pending = this.mutationQueues.get(id);

    if (pending) {
      await pending;
    }
  }

  // =========================================================

  // NOTE CREATION

  // =========================================================



  async createClinicalNote(

    request

  ) {

    const response =

      await authFetch(

        this.baseUrl,

        {

          method: 'POST',

          headers:

            this.getJsonHeaders(),

          body:

            JSON.stringify(

              request

            ),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical note could not be created.'

      );

    }



    return data || {};

  }



  // =========================================================

  // NOTE HEADER / DETAIL

  // =========================================================



  async getClinicalNote(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical note could not be loaded.'

      );

    }



    return data || {};

  }



  async getClinicalNoteDetail(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/detail`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical note details could not be loaded.'

      );

    }



    return data || {};

  }



  // =========================================================

  // SUPERBILL LOOKUP

  // =========================================================



  async getClinicalNoteBySuperbill(

    superbillId

  ) {

    const id =

      requireSuperbillId(

        superbillId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/superbill/${id}`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical note could not be loaded for this superbill.'

      );

    }



    return data || {};

  }



  async existsForSuperbill(

    superbillId

  ) {

    const id =

      requireSuperbillId(

        superbillId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/superbill/${id}/exists`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Unable to determine whether this superbill already has a clinical note.'

      );

    }



    return Boolean(data);

  }



  /**
   * Resolve a visit selected from the episode navigator.
   *
   * The caller owns the explicit user confirmation. This method never creates
   * documentation unless confirmCreate returns true. The backend remains the
   * authority for patient ownership, episode linking and create eligibility.
   *
   * Returns { note, created, cancelled }. A cancelled operation leaves the
   * current page unchanged. The caller must finish its save-before-leaving
   * barrier before calling this method.
   */
  async resolveEpisodeVisitNote(visit, { confirmCreate } = {}) {
    if (!visit || typeof visit !== 'object') {
      throw new Error('A valid episode visit is required.');
    }

    const expectedEpisodeId = requireClinicalEpisodeId(visit.clinicalEpisodeId);
    const assertSameEpisode = (note) => {
      const actualEpisodeId = Number(
        note?.clinicalEpisodeId ?? note?.note?.clinicalEpisodeId ??
        note?.clinicalNote?.clinicalEpisodeId
      );
      if (actualEpisodeId !== expectedEpisodeId) {
        throw new Error('The selected visit does not belong to the current clinical episode.');
      }
      return note;
    };

    const noteId = Number(visit.clinicalNoteId);
    if (Number.isSafeInteger(noteId) && noteId > 0) {
      const note = await this.getClinicalNote(noteId);
      return { note: assertSameEpisode(note), created: false, cancelled: false };
    }

    const superbillId = requireSuperbillId(visit.superbillId);
    const loadExisting = async () => {
      if (!(await this.existsForSuperbill(superbillId))) return null;
      return assertSameEpisode(await this.getClinicalNoteBySuperbill(superbillId));
    };

    // Another staff member may have created the note since the visit list loaded.
    const alreadyExists = await loadExisting();
    if (alreadyExists) {
      return { note: alreadyExists, created: false, cancelled: false };
    }

    // Never silently create a note on a navigation click.
    if (typeof confirmCreate !== 'function') {
      throw new Error('Confirmation is required before creating a Draft clinical note.');
    }
    const approved = await confirmCreate(visit);
    if (approved !== true) {
      return { note: null, created: false, cancelled: true };
    }

    // Recheck after confirmation in case another staff member created it.
    const createdDuringConfirmation = await loadExisting();
    if (createdDuringConfirmation) {
      return { note: createdDuringConfirmation, created: false, cancelled: false };
    }

    try {
      const note = await this.createClinicalNote({ superbillId });
      return { note: assertSameEpisode(note), created: true, cancelled: false };
    } catch (createError) {
      // A concurrent create may have won the unique-superbill race.
      const concurrentNote = await loadExisting().catch(() => null);
      if (concurrentNote) {
        return { note: concurrentNote, created: false, cancelled: false };
      }
      throw createError;
    }
  }

  // =========================================================

  // VALIDATION

  // =========================================================



  async validateClinicalNote(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/validation`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical note validation could not be completed.'

      );

    }



    return data || {};

  }



  // =========================================================

  // WORKFLOW

  // =========================================================



  /**

   * Sensitive workflow mutation.

   *

   * Unlike ordinary section autosaves, expectedVersion is NOT

   * replaced here. The backend must verify the exact version that

   * the user reviewed immediately before the transition.

   */

  async submitForReview(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const payload = {

      ...request,

    };



    return this.runSerializedMutation(

      id,

      async () => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/ready-for-review`,

            {

              method: 'PATCH',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify(

                  payload

                ),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical note could not be submitted for review.'

          );

        }



        return data || {};

      }

    );

  }



  /**

   * Sensitive workflow mutation.

   *

   * Keep the caller-supplied expectedVersion unchanged.

   */

  async returnToDraft(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const payload = {

      ...request,

    };



    return this.runSerializedMutation(

      id,

      async () => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/return-to-draft`,

            {

              method: 'PATCH',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify(

                  payload

                ),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical note could not be returned to draft.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // WARNINGS

  // =========================================================



  async getWarnings(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/warnings`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical warnings could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  /**

   * Warning acknowledgement is a reviewed/audited action.

   *

   * Serialize it with other note mutations, but retain the exact

   * expectedVersion supplied by the warning panel.

   */

  async acknowledgeWarning(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const payload = {

      ...request,

    };



    return this.runSerializedMutation(

      id,

      async () => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/warnings/acknowledge`,

            {

              method: 'PATCH',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify(

                  payload

                ),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical warning could not be acknowledged.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // CLINICAL METADATA

  // =========================================================



  async getStrengthGrades() {

    const response =

      await authFetch(

        `${this.baseUrl}/metadata/strength-grades`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Strength grades could not be loaded.'

      );

    }



    if (!Array.isArray(data)) {

      return [];

    }



    return [...data].sort(

      (first, second) =>

        Number(

          first?.order ?? 0

        ) -

        Number(

          second?.order ?? 0

        )

    );

  }



  async getGoalStatuses() {

    const response =

      await authFetch(

        `${this.baseUrl}/metadata/goal-statuses`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Goal statuses could not be loaded.'

      );

    }



    if (!Array.isArray(data)) {

      return [];

    }



    return [...data].sort(

      (first, second) =>

        Number(

          first?.order ?? 0

        ) -

        Number(

          second?.order ?? 0

        )

    );

  }



  // =========================================================

  // PERMANENT IE / RE-EVALUATION HISTORY

  // =========================================================

  /**
   * Load the permanent built-in evaluation/history section.
   *
   * The backend permits this section only for INITIAL_EVALUATION
   * and RE_EVALUATION notes.
   */
  async getEvaluationHistory(
    clinicalNoteId
  ) {
    const id =
      requireClinicalNoteId(
        clinicalNoteId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/${id}/evaluation-history`,
        {
          method: 'GET',
          headers:
            this.getPlainHeaders(),
        }
      );

    const data =
      await parseResponse(
        response
      );

    if (!response.ok) {
      throw createRequestError(
        response,
        data,
        'Evaluation history could not be loaded.'
      );
    }

    return data || {};
  }

  /**
   * Save or autosave the permanent IE / Re-Evaluation history section.
   *
   * This is an ordinary editable-section save. It therefore participates
   * in the shared per-note mutation queue and obtains the newest parent
   * ClinicalNoteMain.entityVersion immediately before the PUT request.
   */
  async saveEvaluationHistory(
    clinicalNoteId,
    request
  ) {
    const id =
      requireClinicalNoteId(
        clinicalNoteId
      );

    if (
      !request ||
      typeof request !== 'object'
    ) {
      throw new Error(
        'Evaluation history save information is required.'
      );
    }

    if (
      request.expectedVersion === null ||
      request.expectedVersion === undefined
    ) {
      throw new Error(
        'The current clinical note version is required before saving evaluation history.'
      );
    }

    const frequencyMinPerWeek =
      normalizeOptionalPositiveWholeNumber(
        request.frequencyMinPerWeek,
        'Minimum treatment frequency'
      );

    const frequencyMaxPerWeek =
      normalizeOptionalPositiveWholeNumber(
        request.frequencyMaxPerWeek,
        'Maximum treatment frequency'
      );

    const durationWeeks =
      normalizeOptionalPositiveWholeNumber(
        request.durationWeeks,
        'Treatment duration'
      );

    /*
     * Partial drafts are valid: either side of the frequency range may
     * remain blank while the therapist is still documenting the note.
     * Only compare the values when both are present.
     */
    if (
      frequencyMinPerWeek !== null &&
      frequencyMaxPerWeek !== null &&
      frequencyMaxPerWeek < frequencyMinPerWeek
    ) {
      throw new Error(
        'Maximum treatment frequency cannot be less than minimum treatment frequency.'
      );
    }

    const payload = {
      subjectiveHistory:
        normalizeOptionalText(
          request.subjectiveHistory
        ),

      aggravatingFactors:
        normalizeOptionalText(
          request.aggravatingFactors
        ),

      medicalHistory:
        normalizeOptionalText(
          request.medicalHistory
        ),

      surgicalHistory:
        normalizeOptionalText(
          request.surgicalHistory
        ),

      mentalCognitiveStatus:
        normalizeOptionalText(
          request.mentalCognitiveStatus
        ),

      frequencyMinPerWeek,
      frequencyMaxPerWeek,
      durationWeeks,

      rehabPotential:
        normalizeOptionalText(
          request.rehabPotential
        ),

      contraindicationsToTherapy:
        normalizeOptionalText(
          request.contraindicationsToTherapy
        ),

      romStrengthComments:
        request.romStrengthComments === undefined
          ? null
          : String(request.romStrengthComments ?? ''),

      plan:
        normalizeOptionalText(
          request.plan
        ),
    };

    return this.runSerializedSectionSave(
      id,
      async (
        currentVersion
      ) => {
        const response =
          await authFetch(
            `${this.baseUrl}/${id}/evaluation-history`,
            {
              method: 'PUT',
              headers:
                this.getJsonHeaders(),
              body:
                JSON.stringify({
                  expectedVersion:
                    currentVersion,

                  ...payload,
                }),
            }
          );

        const data =
          await parseResponse(
            response
          );

        if (!response.ok) {
          throw createRequestError(
            response,
            data,
            'Evaluation history could not be saved.'
          );
        }

        return data || {};
      }
    );
  }


  // =========================================================
  // ATTACHED DAILY NOTE (INITIAL / RE-EVALUATION ONLY)
  // =========================================================

  /** Load the three saved narrative fields. Measurements belong to the IE/RE. */
  async getAttachedDailyNote(clinicalNoteId) {
    const id = requireClinicalNoteId(clinicalNoteId);
    const response = await authFetch(`${this.baseUrl}/${id}/attached-daily`, {
      method: 'GET', headers: this.getPlainHeaders(),
    });
    const data = await parseResponse(response);
    if (!response.ok) {
      throw createRequestError(response, data, 'Attached Daily Note could not be loaded.');
    }
    return data || {};
  }

  /** Serialize attached narrative autosave with all other sections of this note. */
  async saveAttachedDailyNote(clinicalNoteId, request) {
    const id = requireClinicalNoteId(clinicalNoteId);
    if (!request || typeof request !== 'object' ||
        request.expectedVersion === null || request.expectedVersion === undefined) {
      throw new Error('The current clinical note version is required before saving the attached Daily Note.');
    }
    const payload = {
      subjective: normalizeOptionalText(request.subjective),
      assessment: normalizeOptionalText(request.assessment),
      plan: normalizeOptionalText(request.plan),
    };
    return this.runSerializedSectionSave(id, async (currentVersion) => {
      const response = await authFetch(`${this.baseUrl}/${id}/attached-daily`, {
        method: 'PUT', headers: this.getJsonHeaders(),
        body: JSON.stringify({ expectedVersion: currentVersion, ...payload }),
      });
      const data = await parseResponse(response);
      if (!response.ok) {
        throw createRequestError(response, data, 'Attached Daily Note could not be saved.');
      }
      return data || {};
    });
  }

  // =========================================================

  // PAIN SECTION

  // =========================================================



  async getPain(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/pain`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Pain information could not be loaded.'

      );

    }



    return data;

  }



    /**

   * Load the active pain-template configuration for one body part.

   *

   * This is used when a newly created DRAFT clinical note does not

   * yet have a saved ClinicalNotePainMain row.

   *

   * Existing saved pain documentation continues using the template

   * information returned with that saved pain record.

   */

  async getActivePainTemplate(

    bodyPartTemplateId

  ) {

    const id =

      requireTemplateId(

        bodyPartTemplateId,

        'body-part template ID'

      );



    const response =

      await authFetch(

        `${this.baseUrl}/templates/pain/body-part/${id}/active`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'The active pain template could not be loaded.'

      );

    }



    return data || {};

  }



  async savePain(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Pain save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving pain.'

      );

    }



    const payload = {

      bestPain:

        request.bestPain ??

        null,



      currentPain:

        request.currentPain ??

        null,



      worstPain:

        request.worstPain ??

        null,



      painComments:

        request.painComments ??

        null,

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/pain`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Pain information could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // ROM SECTION

  // =========================================================



  async getRom(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/rom`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'ROM measurements could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async saveRom(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'ROM save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving ROM.'

      );

    }



    const romTemplateId =

      requireTemplateId(

        request.romTemplateId,

        'ROM template ID'

      );



    const measurementSide =

      typeof request.measurementSide ===

        'string'

        ? request.measurementSide.trim()

        : '';



    if (!measurementSide) {

      throw new Error(

        'ROM measurement side is required.'

      );

    }



    let measurementValue =

      null;



    if (

      request.measurementValue !==

        null &&

      request.measurementValue !==

        undefined &&

      String(

        request.measurementValue

      ).trim() !== ''

    ) {

      measurementValue =

        Number(

          request.measurementValue

        );



      if (

        !Number.isInteger(

          measurementValue

        )

      ) {

        throw new Error(

          'ROM measurement must be a whole number.'

        );

      }

    }



    const payload = {

      romTemplateId,

      measurementSide,

      measurementValue,



      comments:

        normalizeOptionalText(

          request.comments

        ),

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/rom`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'ROM measurement could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // STRENGTH SECTION

  // =========================================================



  async getStrength(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/strength`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Strength measurements could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async saveStrength(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Strength save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving strength.'

      );

    }



    const strengthTemplateId =

      requireTemplateId(

        request.strengthTemplateId,

        'strength template ID'

      );



    const measurementSide =

      typeof request.measurementSide ===

        'string'

        ? request.measurementSide.trim()

        : '';



    if (!measurementSide) {

      throw new Error(

        'Strength measurement side is required.'

      );

    }



    const payload = {

      strengthTemplateId,

      measurementSide,



      strengthGrade:

        normalizeOptionalEnum(

          request.strengthGrade

        ),



      comments:

        normalizeOptionalText(

          request.comments

        ),

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/strength`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Strength measurement could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // GOALS SECTION

  // =========================================================



  async getGoals(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/goals`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical goals could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async saveGoal(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Goal save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving a goal.'

      );

    }



    const goalTemplateId =

      requireTemplateId(

        request.goalTemplateId,

        'goal template ID'

      );



    const payload = {

      goalTemplateId,



      shortTermTargetValue:

        normalizeOptionalText(

          request.shortTermTargetValue

        ),



      shortTermCurrentValue:

        normalizeOptionalText(

          request.shortTermCurrentValue

        ),



      shortTermStatus:

        normalizeOptionalEnum(

          request.shortTermStatus

        ),



      longTermTargetValue:

        normalizeOptionalText(

          request.longTermTargetValue

        ),



      longTermCurrentValue:

        normalizeOptionalText(

          request.longTermCurrentValue

        ),



      longTermStatus:

        normalizeOptionalEnum(

          request.longTermStatus

        ),



      patientSpecificGoalText:

        normalizeOptionalText(

          request.patientSpecificGoalText

        ),

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/goals`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical goal could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // SPECIAL TESTS SECTION

  // =========================================================



  async getSpecialTests(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/special-tests`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Special tests could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async saveSpecialTest(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Special-test save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving a special test.'

      );

    }



    const specialTestTemplateId =

      requireTemplateId(

        request.specialTestTemplateId,

        'special-test template ID'

      );



    const measurementSide =

      typeof request.measurementSide ===

        'string'

        ? request.measurementSide.trim()

        : '';



    if (!measurementSide) {

      throw new Error(

        'Special-test measurement side is required.'

      );

    }



    const payload = {

      specialTestTemplateId,

      measurementSide,



      testResult:

        normalizeOptionalText(

          request.testResult

        ),



      testComments:

        normalizeOptionalText(

          request.testComments

        ),

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/special-tests`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Special-test result could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // PALPATION SECTION

  // =========================================================



  async getPalpation(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/palpation`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Palpation findings could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async savePalpation(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Palpation save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving palpation.'

      );

    }



    const palpationTemplateId =

      requireTemplateId(

        request.palpationTemplateId,

        'palpation template ID'

      );



    const measurementSide =

      typeof request.measurementSide ===

        'string'

        ? request.measurementSide.trim()

        : '';



    if (!measurementSide) {

      throw new Error(

        'Palpation measurement side is required.'

      );

    }



    const payload = {

      palpationTemplateId,

      measurementSide,



      palpationResult:

        normalizeOptionalText(

          request.palpationResult

        ),



      comments:

        normalizeOptionalText(

          request.comments

        ),

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/palpation`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Palpation finding could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // CLINICAL CONTENT / NARRATIVE SECTION

  // =========================================================



  async getContent(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/content`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical narrative content could not be loaded.'

      );

    }



    return Array.isArray(data)

      ? data

      : [];

  }



  async saveContent(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Clinical content save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving clinical content.'

      );

    }



    const contentTemplateId =

      requireTemplateId(

        request.contentTemplateId,

        'clinical content template ID'

      );



    const payload = {

      contentTemplateId,



      contentText:

        normalizeOptionalText(

          request.contentText

        ),



      patientSpecificModified:

        request

          .patientSpecificModified ===

        true,

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/content`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical narrative content could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // TREATMENT SECTION

  // =========================================================



  async getTreatment(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/treatment`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Treatment information could not be loaded.'

      );

    }



    return data || {};

  }



  async saveTreatment(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Treatment save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving treatment.'

      );

    }



    let treatmentTemplateId =

      null;



    if (

      request.treatmentTemplateId !==

        null &&

      request.treatmentTemplateId !==

        undefined &&

      String(

        request.treatmentTemplateId

      ).trim() !== ''

    ) {

      treatmentTemplateId =

        requireTemplateId(

          request.treatmentTemplateId,

          'treatment template ID'

        );

    }



    let totalTreatmentMinutes =

      null;



    if (

      request.totalTreatmentMinutes !==

        null &&

      request.totalTreatmentMinutes !==

        undefined &&

      String(

        request.totalTreatmentMinutes

      ).trim() !== ''

    ) {

      totalTreatmentMinutes =

        Number(

          request.totalTreatmentMinutes

        );



      if (

        !Number.isInteger(

          totalTreatmentMinutes

        ) ||

        totalTreatmentMinutes < 0

      ) {

        throw new Error(

          'Total treatment minutes must be a non-negative whole number.'

        );

      }

    }



    const payload = {

      treatmentTemplateId,



      treatmentText:

        normalizeOptionalText(

          request.treatmentText

        ),



      totalTreatmentMinutes,

    };



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/treatment`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Treatment information could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // PLAN OF CARE

  // =========================================================



  async getPlanOfCare(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/plan-of-care`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Plan of Care could not be loaded.'

      );

    }



    return data || {};

  }



  async isPlanOfCareRequired(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/plan-of-care/required`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Unable to determine whether a Plan of Care is required.'

      );

    }



    return Boolean(data);

  }



  async savePlanOfCare(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    if (

      !request ||

      typeof request !== 'object'

    ) {

      throw new Error(

        'Plan of Care save information is required.'

      );

    }



    if (

      request.expectedVersion ===

        null ||

      request.expectedVersion ===

        undefined

    ) {

      throw new Error(

        'The current clinical note version is required before saving the Plan of Care.'

      );

    }



    const referringProviderId =

      normalizeOptionalInteger(

        request.referringProviderId,

        'Referring provider ID'

      );



    const completed =

      request.completed ===

      true;



    const payload = {

      certificationText:

        normalizeOptionalText(

          request.certificationText

        ),



      referringProviderName:

        normalizeOptionalText(

          request.referringProviderName

        ),



      referringProviderId,



      certificationStartDate:

        normalizeOptionalDate(

          request.certificationStartDate

        ),



      certificationEndDate:

        normalizeOptionalDate(

          request.certificationEndDate

        ),



      frequencyText:

        normalizeOptionalText(

          request.frequencyText

        ),



      durationText:

        normalizeOptionalText(

          request.durationText

        ),



      planText:

        normalizeOptionalText(

          request.planText

        ),



      physicianSignatureName:

        normalizeOptionalText(

          request.physicianSignatureName

        ),



      physicianSignatureDate:

        normalizeOptionalDate(

          request.physicianSignatureDate

        ),



      completed,

    };



    /**

     * Draft Plan-of-Care autosave behaves like all other editable

     * section saves and obtains the newest parent version.

     *

     * completed=true is different: completion is an explicit reviewed

     * action, so preserve the exact expectedVersion supplied by the

     * Plan-of-Care component.

     */

    if (completed) {

      const expectedVersion =

        request.expectedVersion;



      return this.runSerializedMutation(

        id,

        async () => {

          const response =

            await authFetch(

              `${this.baseUrl}/${id}/plan-of-care`,

              {

                method: 'PUT',

                headers:

                  this.getJsonHeaders(),

                body:

                  JSON.stringify({

                    expectedVersion,



                    ...payload,

                  }),

              }

            );



          const data =

            await parseResponse(

              response

            );



          if (!response.ok) {

            throw createRequestError(

              response,

              data,

              'Plan of Care could not be completed.'

            );

          }



          return data || {};

        }

      );

    }



    return this.runSerializedSectionSave(

      id,

      async (

        currentVersion

      ) => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/plan-of-care`,

            {

              method: 'PUT',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify({

                  expectedVersion:

                    currentVersion,



                  ...payload,

                }),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Plan of Care could not be saved.'

          );

        }



        return data || {};

      }

    );

  }



  // =========================================================

  // PERMANENT SIGNATURE

  // =========================================================



  /**

   * Permanent signing is deliberately serialized with section

   * mutations, but the caller's expectedVersion is preserved.

   *

   * The service must never silently move signing onto a newer note

   * version that the clinician did not review.

   */

  async signClinicalNote(

    clinicalNoteId,

    request

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const payload = {

      ...request,

    };



    return this.runSerializedMutation(

      id,

      async () => {

        const response =

          await authFetch(

            `${this.baseUrl}/${id}/sign`,

            {

              method: 'POST',

              headers:

                this.getJsonHeaders(),

              body:

                JSON.stringify(

                  payload

                ),

            }

          );



        const data =

          await parseResponse(

            response

          );



        if (!response.ok) {

          throw createRequestError(

            response,

            data,

            'Clinical note could not be signed.'

          );

        }



        return data || {};

      }

    );

  }



  async getSignature(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/signature`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Clinical-note signature could not be loaded.'

      );

    }



    return data;

  }



  // =========================================================

  // FINALIZED DOCUMENT DATA

  // =========================================================



  async getFinalView(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/final-view`,

        {

          method: 'GET',

          headers:

            this.getPlainHeaders(),

        }

      );



    const data =

      await parseResponse(

        response

      );



    if (!response.ok) {

      throw createRequestError(

        response,

        data,

        'Final clinical-note view could not be loaded.'

      );

    }



    return data || {};

  }



  // =========================================================

  // FINALIZED HTML PREVIEW

  // =========================================================



  async getFinalPreviewHtml(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/preview`,

        {

          method: 'GET',

          headers: {

            Accept:

              'text/html',

          },

        }

      );



    const html =

      await response.text();



    if (!response.ok) {

      let data =

        null;



      if (html) {

        try {

          data =

            JSON.parse(

              html

            );

        } catch {

          data = {

            message:

              html,

          };

        }

      }



      throw createRequestError(

        response,

        data,

        'Final clinical-note preview could not be loaded.'

      );

    }



    return html;

  }



  // =========================================================

  // FINALIZED PDF

  // =========================================================



  async getPdfBlob(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const response =

      await authFetch(

        `${this.baseUrl}/${id}/pdf`,

        {

          method: 'GET',

          headers: {

            Accept:

              'application/pdf',

          },

        }

      );



    if (!response.ok) {

      const data =

        await parseResponse(

          response

        );



      throw createRequestError(

        response,

        data,

        'Clinical-note PDF could not be generated.'

      );

    }



    const blob =

      await response.blob();



    if (

      !blob ||

      blob.size === 0

    ) {

      throw new Error(

        'The clinical-note PDF was empty.'

      );

    }



    return blob;

  }



  async createPdfObjectUrl(

    clinicalNoteId

  ) {

    const blob =

      await this.getPdfBlob(

        clinicalNoteId

      );



    return URL.createObjectURL(

      blob

    );

  }



  revokeObjectUrl(url) {

    if (

      typeof url === 'string' &&

      url.startsWith(

        'blob:'

      )

    ) {

      URL.revokeObjectURL(

        url

      );

    }

  }



  async downloadPdf(

    clinicalNoteId

  ) {

    const id =

      requireClinicalNoteId(

        clinicalNoteId

      );



    const blob =

      await this.getPdfBlob(

        id

      );



    const objectUrl =

      URL.createObjectURL(

        blob

      );



    const anchor =

      document.createElement(

        'a'

      );



    anchor.href =

      objectUrl;



    anchor.download =

      `clinical-note-${id}.pdf`;



    anchor.style.display =

      'none';



    document.body.appendChild(

      anchor

    );



    try {

      anchor.click();

    } finally {

      anchor.remove();



      window.setTimeout(

        () => {

          URL.revokeObjectURL(

            objectUrl

          );

        },

        1000

      );

    }

  }


  // =========================================================
  // AUTHORIZATION PDF PACKET
  // =========================================================

  async getAuthorizationPdfPacketBlob(
    authId
  ) {
    const id =
      requireAuthorizationId(
        authId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/authorization/${id}/pdf`,
        {
          method: 'GET',
          headers: {
            Accept:
              'application/pdf',
          },
        }
      );

    if (!response.ok) {
      const data =
        await parseResponse(
          response
        );

      throw createRequestError(
        response,
        data,
        'Authorization clinical-note PDF packet could not be generated.'
      );
    }

    const blob =
      await response.blob();

    if (
      !blob ||
      blob.size === 0
    ) {
      throw new Error(
        'The authorization clinical-note PDF packet was empty.'
      );
    }

    return blob;
  }

  async createAuthorizationPdfPacketObjectUrl(
    authId
  ) {
    const blob =
      await this.getAuthorizationPdfPacketBlob(
        authId
      );

    return URL.createObjectURL(
      blob
    );
  }

  async downloadAuthorizationPdfPacket(
    authId
  ) {
    const id =
      requireAuthorizationId(
        authId
      );

    const blob =
      await this.getAuthorizationPdfPacketBlob(
        id
      );

    const objectUrl =
      URL.createObjectURL(
        blob
      );

    const anchor =
      document.createElement(
        'a'
      );

    anchor.href =
      objectUrl;

    anchor.download =
      `clinical-notes-authorization-${id}.pdf`;

    anchor.style.display =
      'none';

    document.body.appendChild(
      anchor
    );

    try {
      anchor.click();
    } finally {
      anchor.remove();

      window.setTimeout(
        () => {
          URL.revokeObjectURL(
            objectUrl
          );
        },
        1000
      );
    }
  }

  // =========================================================
  // CLINICAL-EPISODE PDF PACKET
  // =========================================================

  async getEpisodePdfPacketBlob(
    clinicalEpisodeId
  ) {
    const id =
      requireClinicalEpisodeId(
        clinicalEpisodeId
      );

    const response =
      await authFetch(
        `${this.baseUrl}/episode/${id}/pdf`,
        {
          method: 'GET',
          headers: {
            Accept:
              'application/pdf',
          },
        }
      );

    if (!response.ok) {
      const data =
        await parseResponse(
          response
        );

      throw createRequestError(
        response,
        data,
        'Clinical-episode PDF packet could not be generated.'
      );
    }

    const blob =
      await response.blob();

    if (
      !blob ||
      blob.size === 0
    ) {
      throw new Error(
        'The clinical-episode PDF packet was empty.'
      );
    }

    return blob;
  }

  async createEpisodePdfPacketObjectUrl(
    clinicalEpisodeId
  ) {
    const blob =
      await this.getEpisodePdfPacketBlob(
        clinicalEpisodeId
      );

    return URL.createObjectURL(
      blob
    );
  }

  async downloadEpisodePdfPacket(
    clinicalEpisodeId
  ) {
    const id =
      requireClinicalEpisodeId(
        clinicalEpisodeId
      );

    const blob =
      await this.getEpisodePdfPacketBlob(
        id
      );

    const objectUrl =
      URL.createObjectURL(
        blob
      );

    const anchor =
      document.createElement(
        'a'
      );

    anchor.href =
      objectUrl;

    anchor.download =
      `clinical-notes-episode-${id}.pdf`;

    anchor.style.display =
      'none';

    document.body.appendChild(
      anchor
    );

    try {
      anchor.click();
    } finally {
      anchor.remove();

      window.setTimeout(
        () => {
          URL.revokeObjectURL(
            objectUrl
          );
        },
        1000
      );
    }
  }

}



const clinicalNoteService =

  new ClinicalNoteService();



export {

  ClinicalNoteService,

};



export default clinicalNoteService;